/**
 * The Upload Studio's session, above the routes (Phase 91, wave 5 review
 * fix 2).
 *
 * The save queue and the upload store used to live on the studio page, so
 * leaving the studio by the sidebar or any other link stopped the unsent
 * saves, cancelled the uploads and left their "Saving..." toasts open for
 * good. They live here now, mounted once inside the router for the signed-in
 * app: they keep running after the page is gone, until every save is sent
 * and every file stored, and their toasts resolve as they do.
 *
 *  - The studio page registers its handlers (`useStudioPageHandlers`): the
 *    synced cells after a save, the dialogs for a refusal it can answer,
 *    "Open it" with the walk. With no page, a save that went through only
 *    refreshes its product, a refusal that needs an answer shows as a failed
 *    save with "Open it", and "Open it" opens the product in the studio.
 *  - The upload tray shows here, on any page, while a file moves.
 *  - Closing or reloading the tab asks first while anything is unsent or
 *    moving; nothing asks once the queue is empty.
 *  - A save that went through, and a status read that finds a link newly
 *    published, mark Product sync's artwork reads stale
 *    (`invalidateProductSyncArtworkReads`): its grid shows the new artwork
 *    on Back, even when the publish finished after the operator left.
 *
 * Nothing here reads or writes until the studio is used: an empty queue
 * polls nothing, and the mirror restore only finds what a studio left.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { trpc } from '@/lib/trpc';
import { UploadTray } from '../components/UploadTray';
import { StudioUploadStoreContext, useStudioUploadStoreOwner } from '../hooks/useStudioSourceUpload';
import {
  STATUS_READ_OPTIONS,
  useStudioSaveQueueOwner,
  type StudioQueueEntry,
  type StudioRefusalCause,
  type StudioSaveQueue,
} from '../hooks/useStudioSaveQueue';
import { uploadKeyOf, type StudioUploadStore } from '../lib/studio-upload-store';
import { groupKeyOf } from '../lib/studio-cells';
import { studioPath } from '../lib/studio-walk';
import { invalidateProductSyncArtworkReads } from '@/features/product-sync/lib/artwork-reads';

/**
 * The org the requests carry right now: the tRPC link reads `x-org-id` from
 * localStorage at send time, and the org switcher writes it before React
 * re-renders (lib/trpc-provider.tsx), so this, not the React org, says which
 * org a save would land in.
 */
function headerOrgId(): string | null {
  try {
    return localStorage.getItem('activeOrgId') || localStorage.getItem('selected_org_id') || null;
  } catch {
    return null;
  }
}

/** The upload keys a queued save still needs (final fix A1). */
function neededUploadKeys(entries: readonly StudioQueueEntry[]): Set<string> {
  const keys = new Set<string>();
  for (const e of entries) for (const s of e.saves) if (s.action !== 'use' && s.uploadKey) keys.add(s.uploadKey);
  return keys;
}

/** What the open studio page does with the queue's news. */
export interface StudioPageHandlers {
  openProduct?: (productId: string) => void;
  onSent?: (entry: StudioQueueEntry) => void;
  onFailed?: (entry: StudioQueueEntry) => void;
  /** True when the page asks the operator (the save stays needs-answer). */
  onNeedsAnswer?: (entry: StudioQueueEntry, cause: StudioRefusalCause) => boolean;
  /** True when the page showed the changed note itself. */
  onProductChanged?: (entry: StudioQueueEntry) => boolean;
}

export interface StudioSession {
  uploads: StudioUploadStore;
  queue: StudioSaveQueue;
  /** The open page's handlers (null when it leaves). */
  setPage: (handlers: StudioPageHandlers | null) => void;
  /**
   * "Open it": the open page opens it (with its walk); with no page, the
   * session's own navigate (always mounted) opens it in the studio. Never a
   * full page load (final fix A4).
   */
  openProduct: (productId: string) => void;
}

const StudioSessionContext = createContext<StudioSession | null>(null);

/** The session above the routes, or null outside one (a lone page mount in tests). */
export function useStudioSessionOptional(): StudioSession | null {
  return useContext(StudioSessionContext);
}

export function useStudioSession(): StudioSession {
  const session = useContext(StudioSessionContext);
  if (!session) throw new Error('useStudioSession needs StudioSessionProvider');
  return session;
}

/** Nothing unsent and no file moving. */
function isIdle(session: Pick<StudioSession, 'uploads' | 'queue'>): boolean {
  return session.queue.getSnapshot().entries.length === 0 && session.uploads.moving().length === 0;
}

export function StudioSessionProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const savePlacements = trpc.uploadStudio.savePlacements.useMutation();
  const publishUse = trpc.uploadStudio.publishUse.useMutation();
  const uploads = useStudioUploadStoreOwner();
  const page = useRef<StudioPageHandlers | null>(null);
  // The mirror is the signed-in user's, in this org (a reload restores only theirs).
  const { user } = useAuth();
  const { organization } = useOrganization();
  const storageScope = user?.id && organization?.id ? `${user.id}:${organization.id}` : null;
  // Final fix A5: the scope a request would carry NOW (the header org), read live.
  const userIdRef = useRef(user?.id ?? null);
  userIdRef.current = user?.id ?? null;
  const orgIdRef = useRef(organization?.id ?? null);
  orgIdRef.current = organization?.id ?? null;
  const [scopeNow] = useState(() => () => {
    const org = headerOrgId() ?? orgIdRef.current;
    return userIdRef.current && org ? `${userIdRef.current}:${org}` : null;
  });
  uploads.setScope(scopeNow);

  const openProduct = (productId: string) => {
    if (page.current?.openProduct) page.current.openProduct(productId);
    else navigate(studioPath(productId));
  };
  const openRef = useRef(openProduct);
  openRef.current = openProduct;

  const queue = useStudioSaveQueueOwner({
    uploads,
    storageScope,
    scopeNow,
    send: (input) => savePlacements.mutateAsync(input),
    fetchStatus: (productIds) => utils.uploadStudio.getSaveStatus.fetch({ productIds }, STATUS_READ_OPTIONS),
    publishUse: (input) => publishUse.mutateAsync(input),
    openProduct: (productId) => openRef.current(productId),
    onSent: (entry) => {
      // Once the product is read again it prints from the stored files: their
      // local previews are let go (wave 5 review nit; the store outlives pages).
      void utils.uploadStudio.getProduct.invalidate({ productId: entry.productId }).then(() => {
        for (const s of entry.saves) {
          if (s.action !== 'use' && s.sourceSha256) uploads.release(uploadKeyOf(entry.productId, groupKeyOf(s)), s.sourceSha256);
        }
      });
      // Stored now; the publish may still be running (`onPublished` refreshes again).
      void invalidateProductSyncArtworkReads(utils);
      page.current?.onSent?.(entry);
    },
    onPublished: () => void invalidateProductSyncArtworkReads(utils),
    onFailed: (entry) => page.current?.onFailed?.(entry),
    onNeedsAnswer: (entry, cause) => page.current?.onNeedsAnswer?.(entry, cause) ?? false,
    onProductChanged: (entry) => page.current?.onProductChanged?.(entry) ?? false,
  });

  // Signed in (or the org known) after the queue started: restore that user's saves now,
  // and send the ones of this scope that were held (final fix A5).
  useEffect(() => {
    if (storageScope) queue.restore();
    queue.rescope();
  }, [queue, storageScope]);

  // Final fix A5: the org switcher only fires an event (this provider stays
  // mounted). Saves of the old org are held, not sent under the new one; a file
  // of the old org still uploading is cancelled.
  useEffect(() => {
    const onOrgChanged = () => {
      uploads.rescope();
      queue.rescope();
    };
    window.addEventListener('org-changed', onOrgChanged);
    return () => window.removeEventListener('org-changed', onOrgChanged);
  }, [queue, uploads]);

  // Final fix A1 + A4: with no studio page open, a file no queued save needs is
  // let go, and a save waiting for an answer fails with Open it. Deferred one
  // tick: StrictMode (and a remount) unregisters and registers the page again.
  const pageGoneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The session itself is gone (signed out): a page leaving after it does nothing.
  const disposed = useRef(false);
  useEffect(
    () =>
      queue.subscribe(() => {
        if (!page.current) uploads.retainOnly(neededUploadKeys(queue.getSnapshot().entries));
      }),
    [queue, uploads],
  );
  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
      if (pageGoneTimer.current) clearTimeout(pageGoneTimer.current);
    };
  }, []);

  // Closing or reloading the tab asks first while a save is unsent or a file moves.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isIdle({ uploads, queue })) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [queue, uploads]);

  const value = useMemo<StudioSession>(
    () => ({
      uploads,
      queue,
      setPage: (handlers) => {
        page.current = handlers;
        if (pageGoneTimer.current) clearTimeout(pageGoneTimer.current);
        pageGoneTimer.current =
          handlers || disposed.current
            ? null
            : setTimeout(() => {
                pageGoneTimer.current = null;
                if (page.current || disposed.current) return;
                queue.failAwaiting();
                uploads.retainOnly(neededUploadKeys(queue.getSnapshot().entries));
              }, 0);
      },
      openProduct: (productId) => openRef.current(productId),
    }),
    [queue, uploads],
  );

  return (
    <StudioSessionContext.Provider value={value}>
      <StudioUploadStoreContext.Provider value={uploads}>
        {children}
        <UploadTray uploads={uploads} />
      </StudioUploadStoreContext.Provider>
    </StudioSessionContext.Provider>
  );
}
